import { Link } from 'react-router-dom'
import { ExternalLink, Loader2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import Card from 'app/frontend/base/components/Card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { awaitPendingJob, displayErrorToast } from 'app/frontend/base/helpers'
import {
  deleteEventRaidReport,
  linkEventRaidReport,
  syncEventRaidReports,
} from 'app/frontend/base/api/event'
import capitalize from 'lodash/capitalize'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

const WCL_REPORT_RETRIEVAL_THRESHOLD = 10

const formatDuration = (startTime: string, endTime: string) => {
  const durationInMinutes = Math.floor(
    (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000 / 60,
  )

  if (durationInMinutes >= 60) {
    return `${Math.round(durationInMinutes / 60)}h`
  }

  return `${durationInMinutes}m`
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  reports: wowaudit.RaidReport[]
  event?: wowaudit.Event
  isLoading: boolean
}

const EventRaidReportsDialog = ({
  open,
  onOpenChange,
  reports,
  event,
  isLoading,
}: Props) => {
  const { t } = useTranslation()
  const { accessLevel } = useAuth()
  const { team, guild } = useTeam()
  const queryClient = useQueryClient()
  const teamId = team.id
  const eventId = event?.id
  const hasAdminAccess = accessLevel === 'admin'
  const hasLinkedWclGuild = !!team.wclGuildId && !!team.wclLinkedAccountId
  const premiumPoints = guild.premiumPoints || 0
  const hasEnoughPremiumPoints = premiumPoints >= WCL_REPORT_RETRIEVAL_THRESHOLD
  const canManageReports = hasAdminAccess && hasEnoughPremiumPoints
  const activeActivities = (event?.activities || []).filter(
    (activity) => activity.active,
  )
  const [syncingNow, setSyncingNow] = useState(false)
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false)
  const [reportUrl, setReportUrl] = useState('')
  const [linkingNow, setLinkingNow] = useState(false)
  const [confirmUnlinkReportCode, setConfirmUnlinkReportCode] = useState<
    string | null
  >(null)
  const [unlinkingReportCode, setUnlinkingReportCode] = useState<string | null>(
    null,
  )

  const awaitJob = (jobId: string) =>
    new Promise<wowaudit.JobStatus<unknown>>((resolve) => {
      awaitPendingJob(jobId, resolve)
    })

  const refreshReports = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['eventRaidReports'],
    })
    await queryClient.refetchQueries({
      queryKey: ['eventRaidReports'],
      type: 'active',
    })
  }

  const onSyncNow = async () => {
    if (!eventId) return

    setSyncingNow(true)
    try {
      const response = await syncEventRaidReports(teamId, eventId)
      const result = await awaitJob(response.job_id)
      if (result.error) {
        throw new Error(result.error)
      }
      await refreshReports()
      toast.success('Report sync completed')
    } catch (error) {
      displayErrorToast(error as Error, t)
    } finally {
      setSyncingNow(false)
    }
  }

  const onLinkNow = async () => {
    if (!eventId || reportUrl.trim() === '') return

    setLinkingNow(true)
    try {
      const response = await linkEventRaidReport(teamId, eventId, {
        reportUrl: reportUrl.trim(),
      })
      const result = await awaitJob(response.job_id)
      if (result.error) {
        throw new Error(result.error)
      }
      await refreshReports()
      setReportUrl('')
      setLinkPopoverOpen(false)
      toast.success('Report linked')
    } catch (error) {
      displayErrorToast(error as Error, t)
    } finally {
      setLinkingNow(false)
    }
  }

  const onUnlinkNow = async () => {
    if (!eventId || !confirmUnlinkReportCode) return

    setUnlinkingReportCode(confirmUnlinkReportCode)
    try {
      await deleteEventRaidReport(teamId, eventId, confirmUnlinkReportCode)
      await refreshReports()
      setConfirmUnlinkReportCode(null)
      toast.success('Report unlinked')
    } catch (error) {
      displayErrorToast(error as Error, t)
    } finally {
      setUnlinkingReportCode(null)
    }
  }

  const manageReportsActions = canManageReports ? (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center gap-2 justify-end'>
        <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={syncingNow || linkingNow || !eventId}
            >
              Link manually
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-80 p-3' align='start'>
            <div className='space-y-2'>
              <Input
                value={reportUrl}
                onChange={(event) => setReportUrl(event.target.value)}
                placeholder='Paste report URL here'
                disabled={syncingNow || linkingNow}
              />
              <Button
                type='button'
                size='sm'
                className='w-full'
                onClick={onLinkNow}
                loading={linkingNow}
                disabled={syncingNow || linkingNow || reportUrl.trim() === ''}
              >
                Link
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          type='button'
          variant='outline'
          size='sm'
          loading={syncingNow}
          onClick={onSyncNow}
          disabled={linkingNow || !eventId}
        >
          Sync now
        </Button>
      </div>

      {syncingNow && (
        <div className='flex flex-col items-center gap-2'>
          <p className='text-sm text-muted-foreground text-center'>
            This sync can take several minutes. You are free to close this
            window and come back later.
          </p>
        </div>
      )}
    </div>
  ) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Warcraft Logs reports</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
          </div>
        ) : reports.length === 0 ? (
          hasEnoughPremiumPoints ? (
            <>
              <Alert variant='info'>
                <AlertTitle>No report linked yet</AlertTitle>
                <AlertDescription className='space-y-1'>
                  {hasAdminAccess && !hasLinkedWclGuild ? (
                    <div>
                      You can link your Warcraft Logs guild to this team in the{' '}
                      <Link
                        className='text-primary hover:text-primary/90'
                        to={`${team.path}/settings#events`}
                        onClick={() => onOpenChange(false)}
                      >
                        settings
                      </Link>
                      .
                    </div>
                  ) : hasLinkedWclGuild ? (
                    <div>Reports are synced automatically once per day</div>
                  ) : (
                    <div>
                      A team admin should link your Warcraft Logs guild to this
                      team in the settings
                    </div>
                  )}
                </AlertDescription>
              </Alert>
              {manageReportsActions}
            </>
          ) : (
            <Alert variant='warning'>
              <AlertTitle>Premium feature</AlertTitle>
              <AlertDescription className='flex items-center justify-between gap-2'>
                This report retrieval requires more premium points.
                <Link
                  to={`${team.path}/premium`}
                  onClick={() => onOpenChange(false)}
                >
                  <Button
                    variant='outline'
                    type='button'
                    size='sm'
                    className='text-foreground'
                  >
                    Premium
                  </Button>
                </Link>
              </AlertDescription>
            </Alert>
          )
        ) : (
          <div className='flex flex-col gap-3'>
            {reports.map((report) => (
              <Card key={report.code} className='p-3 flex flex-col gap-3'>
                <div className='flex items-center justify-between gap-2'>
                  <div className='min-w-0'>
                    <div className='font-medium truncate'>{report.title}</div>
                    <div className='text-xs text-muted-foreground'>
                      Duration:{' '}
                      {formatDuration(report.startTime, report.endTime)}
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <a
                      href={`https://www.warcraftlogs.com/reports/${report.code}`}
                      target='_blank'
                      rel='noreferrer'
                      className='text-muted-foreground hover:text-foreground'
                    >
                      <ExternalLink className='h-4 w-4' />
                    </a>

                    {hasAdminAccess && report.code ? (
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='h-7 w-7 text-muted-foreground hover:text-destructive-foreground'
                        onClick={() =>
                          setConfirmUnlinkReportCode(report.code ?? null)
                        }
                        disabled={unlinkingReportCode === report.code}
                      >
                        {unlinkingReportCode === report.code ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <Trash2 className='h-4 w-4' />
                        )}
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className='flex gap-2'>
                  {activeActivities.map((activity) => {
                    const reportActivity = report.activities.find(
                      (entry) => entry.id === activity.id,
                    )

                    return (
                      <Card
                        key={activity.id}
                        className='self-stretch flex flex-col items-center p-0 pb-2'
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex'>
                              <div className='relative'>
                                {activity.encounter?.thumbnailUrl ||
                                activity.instance?.tileUrl ? (
                                  <img
                                    src={
                                      activity.encounter?.thumbnailUrl ??
                                      activity.instance?.tileUrl ??
                                      undefined
                                    }
                                    className='w-14 h-14 rounded-t-md border-b border-border'
                                  />
                                ) : (
                                  <div className='w-14 h-14 rounded-t-md border-b border-border bg-muted' />
                                )}
                                <div className='absolute bottom-px right-1 text-sm font-semibold px-1 py-0.5 leading-none rounded-t-[8px] bg-card-nested/70'>
                                  {activity.difficulty?.[0].toUpperCase() ??
                                    '-'}
                                </div>
                              </div>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {activity.encounter?.name ||
                              activity.instance?.name ||
                              capitalize('raids')}
                          </TooltipContent>
                        </Tooltip>

                        <div
                          className={cn(
                            'text-xs text-muted-foreground mt-2',
                            reportActivity?.pulls && reportActivity.pulls > 0
                              ? 'text-accent-foreground'
                              : 'text-muted-foreground',
                          )}
                        >
                          Pulls: {reportActivity?.pulls || 0}
                        </div>
                        <div
                          className={cn(
                            'text-xs text-muted-foreground',
                            reportActivity?.kills && reportActivity.kills > 0
                              ? 'text-success-foreground'
                              : 'text-muted-foreground',
                          )}
                        >
                          Kills: {reportActivity?.kills || 0}
                        </div>
                      </Card>
                    )
                  })}
                </div>
              </Card>
            ))}
            {manageReportsActions}
          </div>
        )}
      </DialogContent>
      <ConfirmDialog
        open={confirmUnlinkReportCode !== null}
        title='Unlink report'
        description='If you do not link another report manually, the automatic sync might link this report again later.'
        variant='destructive'
        onCancel={() => setConfirmUnlinkReportCode(null)}
        onContinue={onUnlinkNow}
      />
    </Dialog>
  )
}

export default EventRaidReportsDialog
